import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage, getDownloadURL, ref } from '@angular/fire/storage';
import Fuse from 'fuse.js';
import * as JSZip from 'jszip';
import { DbExercise } from 'src/models/db-exercises.model';

@Injectable({
  providedIn: 'root',
})
export class DbExercisesService {
  private exercisesMap = new Map<string, DbExercise>();
  public filterCategories = {
    force: new Set<string>(),
    level: new Set<string>(),
    mechanic: new Set<string>(),
    equipment: new Set<string>(),
    primaryMuscles: new Set<string>(),
    secondaryMuscles: new Set<string>(),
    category: new Set<string>(),
  };
  private fuse!: Fuse<DbExercise>;
  constructor(private http: HttpClient, private storage: Storage) {}

  private initFuse(exercises: DbExercise[]): void {
    const options = {
      includeScore: true,
      keys: ['name'], // Add other keys here as needed
      threshold: 0.3, // Adjust this value based on the desired typo tolerance
    };
    this.fuse = new Fuse(exercises, options);
  }

  async fetchAndDecompressData(): Promise<DbExercise[]> {
    const fileRef = ref(this.storage, 'exercises.json.zip'); // Create a reference to the file
    const url = await getDownloadURL(fileRef); // Get the download URL as a Promise, no need for firstValueFrom

    const response = await this.http
      .get(url, { responseType: 'blob' })
      .toPromise();
    const zip = await JSZip.loadAsync(response as Blob); // Decompress the blob
    const filename = Object.keys(zip.files)[0];

    if (!filename) {
      throw new Error('Zip file is empty or does not contain any files');
    }

    const jsonStr = await zip.files[filename].async('string'); // Read the decompressed content as a string
    const data = JSON.parse(jsonStr) as DbExercise[]; // Parse the JSON string into an object

    // After decompressing and parsing JSON
    data.forEach((exercise: DbExercise) => {
      this.exercisesMap.set(exercise.id, exercise);

      if (exercise.force) this.filterCategories.force.add(exercise.force);
      if (exercise.level) this.filterCategories.level.add(exercise.level);
      if (exercise.mechanic)
        this.filterCategories.mechanic.add(exercise.mechanic);
      if (exercise.equipment)
        this.filterCategories.equipment.add(exercise.equipment);

      exercise.primaryMuscles.forEach((muscle) => {
        if (muscle) this.filterCategories.primaryMuscles.add(muscle);
      });

      exercise.secondaryMuscles.forEach((muscle) => {
        if (muscle) this.filterCategories.secondaryMuscles.add(muscle);
      });

      if (exercise.category)
        this.filterCategories.category.add(exercise.category);
    });

    this.initFuse(Array.from(this.exercisesMap.values()));

    return data; // Or handle the data as needed
  }

  getExerciseById(id: string): DbExercise | undefined {
    return this.exercisesMap.get(id);
  }

  filterExercises(criteria: {
    [key: string]: string | string[];
  }): DbExercise[] {
    let filteredExercises = Array.from(this.exercisesMap.values());

    // Handle name search with Fuse.js separately
    if ('name' in criteria) {
      const nameSearchResults = this.fuse.search(criteria['name'].toString());
      filteredExercises = nameSearchResults.map((result) => result.item);
    }

    // Apply additional filtering criteria if any
    Object.keys(criteria).forEach((key) => {
      if (key !== 'name') {
        // Skip the name key since it's already handled
        filteredExercises = filteredExercises.filter((exercise) => {
          if (Array.isArray(criteria[key])) {
            return (criteria[key] as string[]).some(
              (value) =>
                exercise[key as keyof DbExercise] === value ||
                exercise[key as keyof DbExercise].includes(value)
            );
          } else {
            return exercise[key as keyof DbExercise] === criteria[key];
          }
        });
      }
    });

    return filteredExercises;
  }

  async getImageUrl(imagePath: string): Promise<string> {
    if (!imagePath) {
      return 'default-image-path.jpg'; // Return a default image path if imagePath is empty
    }

    const fullPath = `/db_exercises/exercises/${imagePath}`;
    try {
      const fileRef = ref(this.storage, fullPath);
      return await getDownloadURL(fileRef);
    } catch (err) {
      console.error(err);
      return 'default-image-path.jpg'; // Return a default image path in case of error
    }
  }
}
